import type {
  ExpenseBody,
  GenesisBody,
  GhostAddBody,
  GhostClaimBody,
  KeyRotateBody,
  MemberAddBody,
  MemberLeaveBody,
  MemberRemoveBody,
  Op,
  PaymentAckBody,
  PaymentBody,
  ProfileBody,
  Split,
  SupersedeBody,
} from "./op.js";
import type { OpStore } from "./store.js";

export interface MemberInfo {
  pubKey: string; // for a ghost this is the ghost id, not a real key
  name: string;
  active: boolean;
  vpa: string;
  isGhost: boolean;
}

export interface ExpenseView {
  id: string;
  desc: string;
  amount: number;
  paidBy: string;
  split: Split;
  author: string;
  ts: number;
  lamport: number;
  voided: boolean;
  edited: boolean;
}

export interface PaymentView {
  id: string;
  from: string;
  to: string;
  amount: number;
  note: string;
  author: string;
  ts: number;
  lamport: number;
  voided: boolean;
  edited: boolean;
  acked: boolean; // receiver signed a payment_ack: the second tick
}

export interface GroupState {
  groupId: string;
  name: string;
  currency: string;
  members: MemberInfo[]; // sorted by display name, then pubkey; includes unclaimed ghosts
  balances: Map<string, number>; // pubkey -> net paise; positive = is owed
  claims: Map<string, string>; // ghost id -> member pubkey (for resolving old entries)
  expenses: ExpenseView[]; // newest first
  payments: PaymentView[]; // newest first
  epoch: number; // current key epoch; 0 means the founding key
  rotations: Op[]; // every key_rotate in topo order, so a member can unwrap any epoch
  removals: Removal[]; // evictions, in topo order, for the audit trail
}

// Who threw whom out, and what they said about it. Kept because an eviction
// with no admin role behind it has to be answerable to the group.
export interface Removal {
  member: string;
  by: string;
  reason: string;
  at: number;
}

// Shares for an expense, in paise, summing EXACTLY to the amount.
// Equal split uses largest-remainder: everyone gets floor(amount/n); the first
// `amount mod n` participants in sorted-pubkey order get one extra paisa. The
// assignment is arbitrary but deterministic - every replica computes the same.
export function computeShares(amount: number, split: Split): Map<string, number> {
  const shares = new Map<string, number>();
  if (split.type === "exact") {
    split.participants.forEach((p, i) => shares.set(p, split.amounts[i] as number));
    return shares;
  }
  const sorted = [...split.participants].sort();
  const n = sorted.length;
  const base = Math.floor(amount / n);
  const remainder = amount - base * n;
  sorted.forEach((p, i) => shares.set(p, base + (i < remainder ? 1 : 0)));
  return shares;
}

export function reduce(store: OpStore): GroupState {
  const ordered = store.topoOrder();
  let name = "";
  let currency = "INR";
  const active = new Map<string, boolean>();
  const addNames = new Map<string, string>();
  const profileNames = new Map<string, string>();
  const vpas = new Map<string, string>();
  const ghostNames = new Map<string, string>();
  const ghostsActive = new Set<string>();
  const claims = new Map<string, string>();
  const acks = new Set<string>(); // payment op hashes with a valid receiver ack
  const rotations: Op[] = [];
  const removals: Removal[] = [];
  let epoch = 0;

  interface Entry {
    op: Op;
    effective: ExpenseBody | PaymentBody | null; // null = voided
    edited: boolean;
  }
  const entries = new Map<string, Entry>();
  const supersedes: Op[] = [];

  for (const op of ordered) {
    switch (op.kind) {
      case "genesis": {
        const body = op.body as GenesisBody;
        name = body.name;
        currency = body.currency;
        active.set(op.author, true);
        break;
      }
      case "member_add": {
        const body = op.body as MemberAddBody;
        active.set(body.member, true);
        if (!addNames.has(body.member)) addNames.set(body.member, body.name);
        break;
      }
      case "member_leave": {
        const body = op.body as MemberLeaveBody;
        active.set(body.member, false);
        break;
      }
      case "member_remove": {
        const body = op.body as MemberRemoveBody;
        active.set(body.member, false);
        removals.push({ member: body.member, by: op.author, reason: body.reason, at: op.ts });
        break;
      }
      case "key_rotate": {
        const body = op.body as KeyRotateBody;
        rotations.push(op);
        // Later-in-topo-order wins, the same rule supersede uses. Concurrent
        // rotations at the same epoch both stay in the ledger and both stay
        // decryptable; this only decides which key new writes use.
        if (body.epoch >= epoch) epoch = body.epoch;
        break;
      }
      case "ghost_add": {
        const body = op.body as GhostAddBody;
        ghostsActive.add(body.ghost);
        if (!ghostNames.has(body.ghost)) ghostNames.set(body.ghost, body.name);
        break;
      }
      case "ghost_claim": {
        const body = op.body as GhostClaimBody;
        // first claim in topo order wins; same rule as validation
        if (ghostsActive.has(body.ghost) && !claims.has(body.ghost)) {
          claims.set(body.ghost, body.member);
          ghostsActive.delete(body.ghost);
        }
        break;
      }
      case "payment_ack":
        acks.add((op.body as PaymentAckBody).target);
        break;
      case "profile": {
        const body = op.body as ProfileBody;
        if (body.name !== "") profileNames.set(op.author, body.name);
        vpas.set(op.author, body.vpa);
        break;
      }
      case "expense":
      case "payment":
        entries.set(op.hash, { op, effective: op.body as ExpenseBody | PaymentBody, edited: false });
        break;
      case "supersede":
        supersedes.push(op);
        break;
    }
  }

  // Apply supersedes in topo order - for competing edits of the same entry the
  // topologically latest wins, identically on every replica.
  for (const sup of supersedes) {
    const body = sup.body as SupersedeBody;
    const entry = entries.get(body.target);
    if (!entry) continue; // validated at ingest; defensive here
    entry.effective = body.replacement;
    entry.edited = true;
  }

  // Balances. Positive: the group owes this person. Negative: they owe.
  const balances = new Map<string, number>();
  const credit = (pub: string, amount: number) =>
    balances.set(pub, (balances.get(pub) ?? 0) + amount);
  for (const pub of active.keys()) balances.set(pub, 0);
  for (const ghost of ghostsActive) balances.set(ghost, 0);

  const expenses: ExpenseView[] = [];
  const payments: PaymentView[] = [];
  for (const { op, effective, edited } of entries.values()) {
    const voided = effective === null;
    if (op.kind === "expense") {
      const body = (effective ?? op.body) as ExpenseBody;
      if (!voided) {
        credit(body.paidBy, body.amount);
        for (const [participant, share] of computeShares(body.amount, body.split)) {
          credit(participant, -share);
        }
      }
      expenses.push({
        id: op.hash,
        desc: body.desc,
        amount: body.amount,
        paidBy: body.paidBy,
        split: body.split,
        author: op.author,
        ts: op.ts,
        lamport: op.lamport,
        voided,
        edited,
      });
    } else {
      const body = (effective ?? op.body) as PaymentBody;
      if (!voided) {
        credit(body.from, body.amount);
        credit(body.to, -body.amount);
      }
      payments.push({
        id: op.hash,
        from: body.from,
        to: body.to,
        amount: body.amount,
        note: body.note,
        author: op.author,
        ts: op.ts,
        lamport: op.lamport,
        voided,
        edited,
        acked: acks.has(op.hash),
      });
    }
  }

  // Fold claimed ghosts into the member who claimed them: history keeps the
  // ghost id on old entries, but the balance follows the person.
  for (const [ghost, member] of claims) {
    if (balances.has(ghost)) {
      balances.set(member, (balances.get(member) ?? 0) + (balances.get(ghost) ?? 0));
      balances.delete(ghost);
    }
  }

  // Internal invariant, not input validation: every credit has a matching
  // debit, so the ledger must always sum to zero. A violation is a bug.
  let sum = 0;
  for (const v of balances.values()) sum += v;
  if (sum !== 0) throw new Error(`ledger invariant broken: balances sum to ${sum}`);

  const memberName = (pub: string) => profileNames.get(pub) ?? addNames.get(pub) ?? "";
  const members: MemberInfo[] = [...active.entries()]
    .map(([pubKey, isActive]) => ({
      pubKey,
      name: memberName(pubKey),
      active: isActive,
      vpa: vpas.get(pubKey) ?? "",
      isGhost: false,
    }))
    .concat(
      [...ghostsActive].map((ghost) => ({
        pubKey: ghost,
        name: ghostNames.get(ghost) ?? "",
        active: true,
        vpa: "",
        isGhost: true,
      })),
    )
    .sort((a, b) => (a.name === b.name ? (a.pubKey < b.pubKey ? -1 : 1) : a.name < b.name ? -1 : 1));

  const newestFirst = <T extends { lamport: number; id: string }>(list: T[]) =>
    list.sort((a, b) => (b.lamport === a.lamport ? (a.id < b.id ? -1 : 1) : b.lamport - a.lamport));

  return {
    groupId: store.groupId,
    name,
    currency,
    members,
    balances,
    claims,
    expenses: newestFirst(expenses),
    payments: newestFirst(payments),
    epoch,
    rotations,
    removals,
  };
}
